import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { IonInput, IonButton, IonIcon } from '@ionic/react';
import { checkmarkOutline, closeOutline } from 'ionicons/icons';
import type { Phone } from '@/modules/phone';
import { PhoneLogic } from '@/modules/phone';
import { pxToRem } from '@/modules/config/constants';

interface PhoneInputProps {
  value?: string;
  onPhoneChange?: (phone: string) => void;
  placeholder?: string;
  required?: boolean;
  label?: string;
  autoFocus?: boolean;
  showValidation?: boolean;
  showControls?: boolean;
  onDone?: (phone: Phone) => void;
  onCancel?: () => void;
  onValidityChange?: (validPhone: Phone | null) => void;
}

/**
 * Formats a phone number as user types: (555) 123-4567
 * Automatically adds parentheses and dashes
 *
 * SINGLE SOURCE OF TRUTH: Enforces 10-digit US phone number limit
 * This component is reused across all phone input fields in the app
 */
const PhoneInput = forwardRef<HTMLIonInputElement, PhoneInputProps>(
  (
    {
      value: externalValue,
      onPhoneChange,
      placeholder = '(555) 123-4567',
      required = false,
      autoFocus = false,
      showValidation = false,
      showControls = false,
      onDone,
      onCancel,
      onValidityChange,
    },
    ref
  ) => {
    // Use internal state if onPhoneChange is not provided (uncontrolled mode)
    const [internalValue, setInternalValue] = useState(externalValue || '');
    const isControlled = onPhoneChange !== undefined;
    const value = isControlled ? externalValue || '' : internalValue;

    // Sync internal value when external value changes (for uncontrolled mode initialization)
    useEffect(() => {
      if (!isControlled && externalValue !== undefined) {
        setInternalValue(externalValue);
      }
    }, [externalValue, isControlled]);

    const formatPhoneNumber = (input: string): string => {
      // Remove all non-digit characters
      const digits = input.replace(/\D/g, '');

      // ENFORCE: Limit to exactly 10 digits (US phone number)
      const limitedDigits = digits.slice(0, 10);

      // Format based on number of digits
      if (limitedDigits.length === 0) {
        return '';
      } else if (limitedDigits.length <= 3) {
        // Format: (XXX
        return `(${limitedDigits}`;
      } else if (limitedDigits.length <= 6) {
        // Format: (XXX) XXX
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
      } else {
        // Format: (XXX) XXX-XXXX
        return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
      }
    };

    /**
     * Helper to count only digits in a string
     * Used for enforcing the 10-digit limit
     */
    const getDigitCount = (str: string): number => {
      return (str || '').replace(/\D/g, '').length;
    };

    // Memoize validPhone to prevent infinite re-renders in useEffect
    // Only recalculate when the value actually changes
    const validPhone = useMemo(() => PhoneLogic.tryCreate(value), [value]);
    const isComplete = validPhone !== null;
    const digitCount = value.replace(/\D/g, '').length;

    /**
     * ENFORCEMENT LAYER 1: Format and limit on input
     * Handles all input methods: typing, pasting, autofill
     */
    const handleInput = (e: CustomEvent) => {
      const inputValue = e.detail.value || '';
      // formatPhoneNumber enforces the 10-digit limit via slice(0, 10)
      const formatted = formatPhoneNumber(inputValue);

      if (isControlled && onPhoneChange) {
        onPhoneChange(formatted);
      } else {
        setInternalValue(formatted);
      }
    };

    /**
     * ENFORCEMENT LAYER 2: Prevent typing beyond limit
     * Provides immediate feedback by blocking the keystroke
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLIonInputElement>) => {
      const nativeEvent = e.nativeEvent;

      // Handle Enter key - same as clicking Done
      if (nativeEvent.key === 'Enter') {
        if (isComplete && onDone) {
          nativeEvent.preventDefault();
          handleDone();
        }
        return;
      }

      // Handle Escape key - same as clicking Cancel
      if (nativeEvent.key === 'Escape') {
        if (onCancel) {
          nativeEvent.preventDefault();
          handleCancel();
        }
        return;
      }

      // Allow: backspace, delete, tab, arrows
      if (
        nativeEvent.key === 'Backspace' ||
        nativeEvent.key === 'Delete' ||
        nativeEvent.key === 'Tab' ||
        nativeEvent.key === 'ArrowLeft' ||
        nativeEvent.key === 'ArrowRight'
      ) {
        return;
      }

      // Allow Ctrl/Cmd shortcuts (copy, paste, etc.)
      if (nativeEvent.ctrlKey || nativeEvent.metaKey) {
        return;
      }

      // Block non-digit characters
      if (!/^\d$/.test(nativeEvent.key)) {
        nativeEvent.preventDefault();
        return;
      }

      // Block additional digits if we already have 10
      if (getDigitCount(value) >= 10) {
        nativeEvent.preventDefault();
      }
    };

    const prevValidPhoneRef = useRef<Phone | null | undefined>(undefined);

    // Notify parent when validity changes
    useEffect(() => {
      if (onValidityChange && prevValidPhoneRef.current !== validPhone) {
        prevValidPhoneRef.current = validPhone;
        onValidityChange(validPhone);
      }
    }, [validPhone, onValidityChange]);

    const handleDone = () => {
      if (isComplete && validPhone && onDone) {
        onDone(validPhone);
      }
    };

    const handleCancel = () => {
      if (onCancel) {
        onCancel();
      }
    };

    return (
      <>
        <IonInput
          ref={ref}
          type="tel"
          value={value}
          onIonInput={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          required={required}
          inputmode="numeric"
          autofocus={autoFocus}
        />
        {showControls && (
          <div slot="end" style={{ display: 'flex', gap: '4px' }}>
            <IonButton
              fill="clear"
              onClick={handleDone}
              disabled={!isComplete}
              color="success"
            >
              <IonIcon icon={checkmarkOutline} />
              Done
            </IonButton>
            <IonButton fill="clear" onClick={handleCancel} color="medium">
              <IonIcon icon={closeOutline} />
              Cancel
            </IonButton>
          </div>
        )}
        {showValidation && value && !isComplete && (
          <p
            style={{
              fontSize: pxToRem(12),
              color: '#eb445a',
              padding: '0 16px',
              margin: '4px 0 0 0',
            }}
          >
            Phone number must be 10 digits ({digitCount}/10)
          </p>
        )}
      </>
    );
  }
);

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
